import { Fragment } from "react";

import { round_ex } from "@datanac/datanac-api-toolkit/dist/utility/Math";
import { useColorScheme, useMediaQuery } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { getCellColor, getHeaderColor } from "../../StressTestVisualization/getCellColor";

export function PlcStressTestGrid({
  title,

  insurance_plan_uuid,
  displayFieldName,

  xAxisValue,
  plc_yield,

  xAxisFieldName,
  xAxisDisplayName,

  yAxisFieldName,
  yAxisDisplayName,

  doMarkNegativeValues,

  stressTestResults,

  maxLossValue,

  // New props for display mode functionality
  displayMode,
  budgetTotalReportedAcreage,
  arcPlcTotalBaseAcreage,

  // Optional prop for highlighting specific row
  highlightRowIndex,
}) {
  const isXXL = useMediaQuery("(min-width:1537px)");
  const { mode } = useColorScheme();
  const maxProfitValue =
    stressTestResults && stressTestResults?.length && stressTestResults[0]?.length
      ? stressTestResults[0][0][displayFieldName] || stressTestResults[0][0]?.indemnityResults?.[0]?.[displayFieldName]
      : 1000;

  const getXAxis = () => {
    if (stressTestResults) {
      const xAxisElements = stressTestResults[0]?.map((row) => {
        return row[xAxisFieldName || "yield"];
      });

      return [xAxisElements[0]];
    }
  };

  // TODO maybe replace line 48-68 it with utility/formatters
  const _priceFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    currencySign: "accounting",
    maximumFractionDigits: 0,
  });
  const _priceFormatter2 = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    currencySign: "accounting",
    maximumFractionDigits: 2,
  });

  const formatPrice = (value) => {
    return value || value === 0 ? _priceFormatter.format(value)?.replace("$", "$ ") : "";
  };

  const formatPrice2 = (value) => {
    return value || value === 0 ? _priceFormatter2.format(value)?.replace("$", "$ ") : "";
  };

  const getDisplayValue = (cell, insurance_plan_uuid, displayFieldName) => {
    let displayValue;
    if (insurance_plan_uuid) {
      const insurancePlan = cell?.indemnityResults?.find((r) => r.insurance_plan_uuid === insurance_plan_uuid);
      if (insurancePlan) {
        displayValue = insurancePlan[displayFieldName];
      }
    } else {
      displayValue = cell[displayFieldName] || cell?.indemnityResults?.[0]?.[displayFieldName];
    }

    return displayValue;
  };

  const CustomWidthTooltip = styled(({ className, ...props }) => (
    <Tooltip
      {...props}
      classes={{ popper: className }}
      enterDelay={1000}
      enterTouchDelay={1000}
      enterNextDelay={1000}
    />
  ))({ [`& .${tooltipClasses.tooltip}`]: { maxWidth: "none" } });

  return (
    <>
      <TableContainer sx={{ boxSizing: "border-box", width: 250 }} className="PlcDataGrid">
        {title && (
          <Box sx={{ p: 1 }}>
            <Typography>{title}</Typography>
          </Box>
        )}
        <Table
          data-testid="PLC-stress-test"
          padding="none"
          spacing={0}
          size="small"
          sx={{
            [`& .${tableCellClasses.root}`]: {
              borderBottom: "none",
              fontSize: {
                xs: "0.75rem",
                sm: "0.875rem",
              },
            },
          }}
        >
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={1}
                sx={{
                  textAlign: "center",
                  padding: "0 !important",
                  paddingTop: "4pt !important",
                  paddingBottom: "4pt !important",
                  paddingRight: "4pt !important",
                  paddingLeft: "4pt !important",
                  backgroundColor: "background.paper",
                  borderRadius: "8px 0 0 0",
                }}
              >
                {yAxisDisplayName || "Price"}
              </TableCell>
              <TableCell
                colSpan="1"
                sx={{
                  textAlign: "center",
                  backgroundColor: "background.paper",
                  borderRadius: "0 8px 0 0",
                }}
              >
                {xAxisDisplayName || "PLC Yield"}
              </TableCell>
            </TableRow>
            <TableRow
              sx={{
                borderBottom: "1px solid",
                borderColor: "colors.borderLight",
              }}
            >
              <TableCell
                sx={{
                  padding: "0 !important",
                  backgroundColor: "background.paper",
                }}
              >
                &nbsp;
              </TableCell>
              {getXAxis()?.map((x, index) => {
                // Get the cell data for this column to show yields in tooltip
                const cellForColumn = stressTestResults?.[0]?.[index];

                return (
                  <CustomWidthTooltip
                    key={`x-axis-tooltip-${index}`}
                    arrow
                    title={
                      cellForColumn ? (
                        <>
                          <Typography>
                            <strong>Yields for this column:</strong>
                            <br />
                            &nbsp;&nbsp;Yield: {cellForColumn["yield"]}
                            {Boolean(cellForColumn["yield_seedcotton"]) && (
                              <>
                                <br />
                                &nbsp;&nbsp;Yield (Seed Cotton): {cellForColumn["yield_seedcotton"]}
                              </>
                            )}
                            <br />
                            <br />
                            <strong>County Yields:</strong>
                            <br />
                            &nbsp;&nbsp;County Yield: {cellForColumn["county_yield"]}
                            {Boolean(cellForColumn["county_yield_seedcotton"]) && (
                              <>
                                <br />
                                &nbsp;&nbsp;County Yield (Seed Cotton): {cellForColumn["county_yield_seedcotton"]}
                              </>
                            )}
                          </Typography>
                        </>
                      ) : null
                    }
                  >
                    <TableCell
                      key={index}
                      sx={{
                        textAlign: "right",
                        padding: "0 !important",
                        paddingTop: "4pt !important",
                        paddingBottom: "4pt !important",
                        paddingRight: "4pt !important",
                        borderBottom: "1px solid var(--mui-palette-grey-) !important",
                        backgroundColor: getHeaderColor(index, 1, mode !== "light"),
                        fontSize: {
                          xs: "0.75rem",
                          sm: "0.75rem",
                        },
                      }}
                    >
                      {round_ex(x, 0)}
                    </TableCell>
                  </CustomWidthTooltip>
                );
              })}
            </TableRow>

            {stressTestResults &&
              stressTestResults?.length > 0 &&
              stressTestResults?.map((row, rowIndex) => {
                const totalYItems = stressTestResults?.length || 0;
                const isLastRow = rowIndex === totalYItems - 1;

                return (
                  <TableRow data-testid={`plc-stress-test-row-${rowIndex}`} key={rowIndex}>
                    <TableCell
                      sx={{
                        borderRight: "1px solid",
                        borderColor: "colors.borderLight",
                        p: 0,
                        textAlign: "center",
                        paddingTop: "0 !important",
                        paddingBottom: "0 !important",
                        paddingLeft: "8px !important",
                        paddingRight: "8px !important",
                        maxWidth: { xs: "3.75rem", md: "2.5rem" },
                        backgroundColor: getHeaderColor(rowIndex, totalYItems, mode !== "light"),
                        fontSize: "0.75rem",
                        borderRadius: isLastRow ? "0 0 0 8px" : "0",
                      }}
                    >
                      {yAxisFieldName === "price" ? formatPrice2(row[0]?.[yAxisFieldName]) : row[0]?.[yAxisFieldName]}
                    </TableCell>
                    {[row[0]].map((cell, index) => {
                      return (
                        <CustomWidthTooltip
                          key={"tooltip" + index}
                          sx={{ maxWidth: 1000 }}
                          arrow
                          title={
                            <>
                              <Typography>
                                <strong>County Yields:</strong>
                                <br />
                                &nbsp;&nbsp;County Yield: {cell["county_yield"]}
                                <br />
                                <br />
                                <strong>Prices:</strong>
                                <br />
                                &nbsp;&nbsp;Effective Price: {formatPrice2(cell["price"])}
                              </Typography>
                              <Divider />
                              <Typography>
                                {cell.indemnityResults &&
                                  cell.indemnityResults.length > 0 &&
                                  cell.indemnityResults.map((r, index) => {
                                    return (
                                      <Fragment key={index}>
                                        PLC
                                        {r.indemnity_total > 0 && (
                                          <>
                                            <br />
                                            Total: {formatPrice(r.indemnity_total)}
                                            <br />
                                            Per Base Acre: {formatPrice(r.indemnity_total_per_base_acre)}
                                            <br />
                                            <br />
                                            Per Planted Acre: {formatPrice2(r.indemnity_total_per_acre)}
                                            <br />
                                          </>
                                        )}
                                        {r.indemnity_total === 0 && (
                                          <>
                                            <br />
                                            No payment
                                          </>
                                        )}
                                      </Fragment>
                                    );
                                  })}
                                <br />
                              </Typography>
                            </>
                          }
                        >
                          <TableCell
                            key={"cell" + index}
                            sx={{
                              borderTop:
                                highlightRowIndex !== null &&
                                highlightRowIndex !== undefined &&
                                rowIndex === highlightRowIndex
                                  ? (theme) => `1px solid ${theme.palette.colors.highlightLime}`
                                  : null,
                              borderBottom:
                                highlightRowIndex !== null &&
                                highlightRowIndex !== undefined &&
                                rowIndex === highlightRowIndex
                                  ? (theme) => `1px solid ${theme.palette.colors.highlightLime} !important`
                                  : null,
                              paddingLeft: "4pt !important",
                              paddingRight: "8pt !important",
                              paddingTop: "8pt !important",
                              paddingBottom: "8pt !important",
                              textAlign: "right",
                              backgroundColor: getCellColor(
                                getDisplayValue(cell, insurance_plan_uuid, displayFieldName),
                                maxLossValue,
                                doMarkNegativeValues,
                                maxProfitValue,
                                false,
                                mode !== "light"
                              ),
                              borderRadius: isLastRow ? "0 0 8px 0" : "0",
                            }}
                          >
                            {formatPrice(getDisplayValue(cell, insurance_plan_uuid, displayFieldName)) || " "}
                            <br />
                          </TableCell>
                        </CustomWidthTooltip>
                      );
                    })}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
